import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import RouterIcon from '@mui/icons-material/Router';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import VideocamIcon from '@mui/icons-material/Videocam';
import SensorsIcon from '@mui/icons-material/Sensors';
import HubIcon from '@mui/icons-material/Hub';
import DevicesIcon from '@mui/icons-material/Devices';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SpeedIcon from '@mui/icons-material/Speed';
import CodeIcon from '@mui/icons-material/Code';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { api } from '../services/api';
import { getAllDeviceTypes } from './ProjectsNetworkStatusMapView';
import AlertDetailsDialog from './AlertDetailsDialog';
import AlertSpanChart from './AlertSpanChart';
import { NEUTRAL, STATUS, SEVERITY_COLORS, statusTint, textFor } from '../theme';


const formatUtcTime = (date) => {
  if (!date) return '--:--:-- UTC';
  return date.toLocaleTimeString('en-US', { hour12: false, timeZone: 'UTC' }) + ' UTC';
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
};

const formatIsoDateTime = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString('en-US', { hour12: false });
};

const toIso = (date) => (date ? new Date(date).toISOString() : null);

// For <input type="datetime-local"> we must provide local time (no timezone suffix).
// We include seconds so refresh-to-now is visible even within the same minute.
const toLocalInputValue = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// Parse <input type="datetime-local"> value as local time.
const fromLocalInputValue = (value) => {
  const s = String(value || '');
  if (!s) return null;
  const [datePart, timePart] = s.split('T');
  if (!datePart || !timePart) return null;
  const [y, m, d] = datePart.split('-').map((x) => parseInt(x, 10));
  const [hh, mm, ss] = timePart.split(':').map((x) => parseInt(x, 10));
  if (![y, m, d, hh, mm].every((n) => Number.isFinite(n))) return null;
  return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, Number.isFinite(ss) ? ss : 0, 0);
};

const formatReceivedAt = (receivedAt) => {
  if (!receivedAt) return '-';
  const seconds = receivedAt._seconds ?? receivedAt.seconds;
  if (typeof seconds === 'number') return new Date(seconds * 1000).toLocaleString('en-US', { hour12: false });
  if (typeof receivedAt === 'string') return receivedAt;
  return '-';
};

const formatDuration = (startedAt, endedAt) => {
  if (!startedAt || !endedAt) return '-';
  const startSeconds = startedAt._seconds ?? startedAt.seconds ?? 0;
  const endSeconds = endedAt._seconds ?? endedAt.seconds ?? 0;
  if (!startSeconds || !endSeconds) return '-';
  
  const durationSeconds = endSeconds - startSeconds;
  const hours = Math.floor(durationSeconds / 3600);
  const minutes = Math.floor((durationSeconds % 3600) / 60);
  const seconds = durationSeconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
};

// Component display names for alerts
const componentDisplayNames = {
  pubsub: 'Pub/Sub',
  dataflow: 'Dataflow',
  dataform: 'Dataform',
  firestore: 'Firestore',
  cloud_functions: 'Cloud Functions',
  gke: 'GKE',
  edge_devices: 'Edge Devices',
  other: 'Other',
};

// Severity colors for alerts
const severityColors = SEVERITY_COLORS;

function defaultStartTime() {
  const d = new Date();
  d.setHours(d.getHours() - 24);
  return d;
}

const formatConnectedTime = (seconds) => {
  if (seconds === null || seconds === undefined) return null;
  const hours = (seconds / 3600).toFixed(1);
  return `${hours}h`;
};

const getDeviceIcon = (deviceType) => {
  const type = (deviceType || '').toLowerCase();
  if (type.includes('gateway') || type.includes('cap')) return <RouterIcon />;
  if (type.includes('mesh') || type.includes('node')) return <WifiTetheringIcon />;
  if (type.includes('camera') || type.includes('ai') || type.includes('nvidia')) return <VideocamIcon />;
  if (type.includes('sensor') || type.includes('hub')) return <SensorsIcon />;
  return <HubIcon />;
};

const getLatencyColor = (latency) => {
  if (latency === null || latency === undefined) return STATUS.unknown;
  // avg_latency_quality is a 0-1 score, not milliseconds
  if (latency > 0.7) return STATUS.healthy;
  if (latency > 0.4) return STATUS.warning;
  return STATUS.critical;
};

const getPacketLossColor = (loss) => {
  if (loss === null || loss === undefined) return STATUS.unknown;
  if (loss < 1) return STATUS.healthy;
  if (loss < 5) return STATUS.warning;
  return STATUS.critical;
};

const getHealthIndicator = (connected, offline) => {
  const total = connected + offline;
  if (total === 0) return { color: STATUS.unknown, label: 'N/A', status: 'unknown' };
  const ratio = (connected / total) * 100;
  // SMART devices: Green (all online) or Red (any offline) - no yellow
  if (ratio >= 100) return { color: STATUS.healthy, label: 'Healthy', status: 'healthy' };
  return { color: STATUS.critical, label: 'Critical', status: 'critical' };
};

const getConnectivityRatio = (connected, offline) => {
  const total = connected + offline;
  if (total === 0) return null;
  return (connected / total) * 100;
};

const computeSystemStatus = (sourceData) => {
  let worstStatus = 'healthy';
  let worstType = null;
  let worstRatio = 100;

  Object.entries(sourceData || {}).forEach(([type, data]) => {
    let connected = 0;
    let offline = 0;

    if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
      connected = typeof data.connected === 'number' ? data.connected : 0;
      offline = typeof data.offline === 'number' ? data.offline : 0;
    } else if (typeof data === 'number') {
      connected = data;
      offline = 0;
    }

    const total = connected + offline;
    if (total === 0) return;
    const ratio = (connected / total) * 100;

    if (ratio < worstRatio) {
      worstRatio = ratio;
      worstType = type;
    }

    if (ratio < 80) {
      worstStatus = 'critical';
    } else if (ratio < 100 && worstStatus !== 'critical') {
      worstStatus = 'degraded';
    }
  });

  if (worstStatus === 'critical') {
    return {
      status: 'critical',
      label: 'System Critical',
      accent: STATUS.critical,
      worstType,
    };
  }
  if (worstStatus === 'degraded') {
    return {
      status: 'degraded',
      label: 'System Degraded',
      accent: STATUS.critical,
      worstType,
    };
  }
  return { status: 'healthy', label: 'System Healthy', accent: STATUS.healthy, worstType: null };
};

const ProjectDetailsDialog = ({ open, onClose, projectId, initialProject, demoMode, onTabControlReady }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [networkData, setNetworkData] = useState(null);
  const [lmsData, setLmsData] = useState(null);
  const [lmsError, setLmsError] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState(null);
  const [alertsData, setAlertsData] = useState(null);
  const [alertsStartTime, setAlertsStartTime] = useState(() => defaultStartTime());
  const [alertsEndTime, setAlertsEndTime] = useState(() => new Date());
  const [alertsRange, setAlertsRange] = useState(null);
  const [alertFilter, setAlertFilter] = useState('all'); // 'all', 'open', 'closed'
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [alertDetailsOpen, setAlertDetailsOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedVersions, setExpandedVersions] = useState({});
  const [expandedGateways, setExpandedGateways] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const alertsInFlightRef = useRef(false);

  const fetchProjectDetails = useCallback(async ({ signal } = {}) => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      setError(null);
      setLmsError(null);

      const [networkResult, lmsResult] = await Promise.allSettled([
        api.getProjectNetworkStatusById(projectId, { signal }),
        api.getProjectLmsStatusById(projectId, { signal }),
      ]);

      if (networkResult.status === 'fulfilled') {
        setNetworkData(networkResult.value);
      } else {
        if (!networkResult.reason?.isAbort) {
          console.error('Error fetching project network details:', networkResult.reason);
          setError(networkResult.reason?.message || 'Failed to fetch project network details');
        }
      }

      if (lmsResult.status === 'fulfilled') {
        setLmsData(lmsResult.value);
      } else {
        if (!lmsResult.reason?.isAbort) {
          console.error('Error fetching project LMS details:', lmsResult.reason);
          setLmsData(null);
          setLmsError(lmsResult.reason?.message || 'Failed to fetch project LMS details');
        }
      }

      setLastUpdated(new Date());
    } catch (err) {
      if (!err?.isAbort) {
        console.error('Error fetching project details:', err);
        setError(err.message || 'Failed to fetch project details');
      }
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open && projectId) {
      setActiveTab(0);
      setAlertsData(null);
      setAlertsError(null);
      setAlertsRange(null);
      setAlertsStartTime(defaultStartTime());
      setAlertsEndTime(new Date());
      setSelectedAlert(null);
      setAlertDetailsOpen(false);
      // Avoid refetching network-status / projectsstats when we already have
      // the merged project object from the parent view (map/list/cards).
      if (initialProject && (initialProject.project_id || '').toLowerCase() === String(projectId).toLowerCase()) {
        setError(null);
        setLmsError(null);
        setNetworkData(initialProject);
        setLmsData(initialProject.lms_data || null);
        setLastUpdated(new Date());
        setLoading(false);
      } else {
        const controller = new AbortController();
        fetchProjectDetails({ signal: controller.signal });
        return () => controller.abort();
      }
    }
    return undefined;
  }, [open, projectId, initialProject, fetchProjectDetails]);

  const fetchProjectAlerts = useCallback(async ({ startTimeOverride, endTimeOverride, signal } = {}) => {
    if (!projectId) return;
    if (alertsInFlightRef.current) return;
    try {
      alertsInFlightRef.current = true;
      setAlertsLoading(true);
      setAlertsError(null);
      const startTime = startTimeOverride || alertsStartTime;
      const endTime = endTimeOverride || alertsEndTime;
      const data = await api.getProjectAlerts(projectId, {
        startTime: toIso(startTime),
        endTime: toIso(endTime),
        limit: 200,
        signal,
      });
      setAlertsData(data);
      setAlertsRange({ start: startTime, end: endTime });
    } catch (e) {
      if (!e?.isAbort) {
        setAlertsError(e.message || 'Failed to fetch project alerts');
      }
    } finally {
      setAlertsLoading(false);
      alertsInFlightRef.current = false;
    }
  }, [projectId, alertsStartTime, alertsEndTime]);

  const refreshAlertsToNow = useCallback(() => {
    const now = new Date();
    setAlertsEndTime(now);
    fetchProjectAlerts({ endTimeOverride: now });
  }, [fetchProjectAlerts]);

  useEffect(() => {
    // Lazy-load alerts when user opens Alerts tab (do not auto-retry after errors)
    if (open && projectId && activeTab === 2 && !alertsData && !alertsError) {
      const controller = new AbortController();
      fetchProjectAlerts({ signal: controller.signal });
      return () => controller.abort();
    }
    return undefined;
  }, [open, projectId, activeTab, alertsData, alertsError, fetchProjectAlerts]);

  const toggleVersionExpand = (deviceType) => {
    setExpandedVersions((prev) => ({ ...prev, [deviceType]: !prev[deviceType] }));
  };

  const toggleGatewayExpand = (gatewayId) => {
    setExpandedGateways((prev) => ({ ...prev, [gatewayId]: !prev[gatewayId] }));
  };

  const devicesByType = useMemo(() => networkData?.devices_by_type || {}, [networkData]);
  const sourceData = useMemo(() => devicesByType, [devicesByType]);

  const systemStatus = useMemo(() => computeSystemStatus(sourceData), [sourceData]);

  // Deduplicate alerts by incidentId - prefer closed state, then most recent receivedAt
  const deduplicatedAlerts = useMemo(() => {
    const alertsRaw = Array.isArray(alertsData?.events) ? alertsData.events : [];
    const alertsMap = new Map();
    alertsRaw.forEach((a) => {
      const incidentId = a.incidentId;
      if (!incidentId) {
        // If no incidentId, use the document ID as fallback
        alertsMap.set(a.id, a);
        return;
      }
      const existing = alertsMap.get(incidentId);
      if (!existing) {
        alertsMap.set(incidentId, a);
      } else {
        // Prefer closed state over open state
        const existingIsClosed = existing.state === 'closed';
        const currentIsClosed = a.state === 'closed';
        
        if (currentIsClosed && !existingIsClosed) {
          // Current is closed, existing is open - prefer closed
          alertsMap.set(incidentId, a);
        } else if (!currentIsClosed && existingIsClosed) {
          // Current is open, existing is closed - keep existing (closed)
          // Don't replace
        } else {
          // Both have same state (both closed or both open) - keep most recent by receivedAt
          const existingReceived = existing.receivedAt?._seconds || existing.receivedAt?.seconds || 0;
          const currentReceived = a.receivedAt?._seconds || a.receivedAt?.seconds || 0;
          if (currentReceived > existingReceived) {
            alertsMap.set(incidentId, a);
          }
        }
      }
    });
    const deduplicated = Array.from(alertsMap.values());
    
    // Apply state filter
    if (alertFilter === 'open') {
      return deduplicated.filter((a) => a.state === 'open');
    } else if (alertFilter === 'closed') {
      return deduplicated.filter((a) => a.state === 'closed');
    }
    return deduplicated;
  }, [alertsData, alertFilter]);

  // Demo mode helpers (must be declared after devicesByType/deduplicatedAlerts to avoid TDZ)
  const expandAllNetwork = useCallback(() => {
    const allTypes = Object.keys(devicesByType || {});
    const expanded = {};
    allTypes.forEach((t) => {
      expanded[t] = true;
    });
    setExpandedVersions(expanded);

    const allGateways = networkData?.gateways || [];
    const gwExpanded = {};
    allGateways.forEach((gw) => {
      gwExpanded[gw.gateway_id] = true;
    });
    setExpandedGateways(gwExpanded);
  }, [devicesByType, networkData]);

  const expandAllLms = useCallback(() => {
    // LMS doesn't have nested expansion currently, just set flag for future use
  }, []);

  const openFirstAlert = useCallback(() => {
    if (deduplicatedAlerts && deduplicatedAlerts.length > 0) {
      setSelectedAlert(deduplicatedAlerts[0]);
      setAlertDetailsOpen(true);
    }
  }, [deduplicatedAlerts]);

  const closeAlertDetails = useCallback(() => {
    setAlertDetailsOpen(false);
    setSelectedAlert(null);
  }, []);

  // Register demo mode controls
  useEffect(() => {
    if (!demoMode || !onTabControlReady) return;
    onTabControlReady({
      setActiveTab,
      expandAllNetwork,
      expandAllLms,
      openFirstAlert,
      closeAlertDetails,
    });
  }, [demoMode, onTabControlReady, expandAllNetwork, expandAllLms, openFirstAlert, closeAlertDetails]);

  // Merge network and LMS data for device type display
  const mergedProjectData = useMemo(() => {
    if (!networkData) return null;
    return {
      ...networkData,
      lms_data: lmsData || null,
    };
  }, [networkData, lmsData]);

  const deviceTypeSummaries = useMemo(() => {
    if (!mergedProjectData) return [];
    
    const statusRank = (status) => {
      if (status === 'critical') return 0;
      if (status === 'warning') return 1;
      if (status === 'healthy') return 2;
      return 3;
    };

    // Get all device types (SMART and NON-SMART) with normalized names
    const { smart, nonSmart } = getAllDeviceTypes(mergedProjectData);
    const allDevices = [...smart, ...nonSmart];

    return allDevices
      .map((deviceInfo) => {
        const isSmart = smart.includes(deviceInfo);
        const connected = deviceInfo.connected || 0;
        const offline = deviceInfo.offline || 0;
        const total = deviceInfo.total || 0;
        
        // Health indicator only for SMART devices
        const health = isSmart 
          ? getHealthIndicator(connected, offline)
          : { color: STATUS.unknown, label: 'N/A', status: 'unknown' };

        return { 
          type: deviceInfo.displayName,
          originalType: deviceInfo.originalType,
          connected, 
          offline, 
          total, 
          health,
          isSmart,
        };
      })
      .sort((a, b) => {
        // SMART first, then NON-SMART
        if (a.isSmart !== b.isSmart) {
          return a.isSmart ? -1 : 1;
        }
        // Then by status (for SMART devices)
        if (a.isSmart && b.isSmart) {
          const byStatus = statusRank(a.health.status) - statusRank(b.health.status);
          if (byStatus !== 0) return byStatus;
        }
        // Then by offline count
        if (b.offline !== a.offline) return b.offline - a.offline;
        // Finally alphabetically
        return a.type.localeCompare(b.type);
      });
  }, [mergedProjectData]);

  // Network quality metrics
  const networkQualityMetrics = networkData?.network_quality_metrics || networkData?.network_quality || {};
  const avgLatencyQuality = networkQualityMetrics.avg_latency_quality ?? networkQualityMetrics.avg_latency ?? null;
  const packetLoss = networkQualityMetrics.avg_packet_loss_pct ?? networkQualityMetrics.avg_packet_loss ?? networkQualityMetrics.packet_loss_percent ?? null;
  
  // Connected time metrics - convert seconds to hours
  const minConnectedTimeSec = networkQualityMetrics.min_connected_time_sec ?? null;
  const avgConnectedTimeSec = networkQualityMetrics.avg_connected_time_sec ?? null;
  const medianConnectedTimeSec = networkQualityMetrics.median_connected_time_sec ?? null;

  // Topology metrics
  const topologyMetrics = networkData?.topology_metrics || networkData?.topology || {};
  const maxNodesPerCap = topologyMetrics.max_connected_nodes_per_cap ?? topologyMetrics.max_nodes_per_cap ?? null;
  const minNodesPerCap = topologyMetrics.min_connected_nodes_per_cap ?? topologyMetrics.min_nodes_per_cap ?? null;
  const maxTreeDepth = topologyMetrics.max_tree_depth_connected ?? topologyMetrics.max_tree_depth ?? topologyMetrics.max_hops ?? null;

  const softwareVersionsByType = networkData?.software_versions_by_type || networkData?.software_versions || {};

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            maxHeight: '95vh',
            height: '95vh',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '1.05rem', lineHeight: 1.2 }}>
                Project {projectId}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.82rem', mt: 0.25 }}>
                Network &amp; Installation Status
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {lastUpdated && (
              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  sx={{
                    fontSize: '0.62rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    color: 'text.secondary',
                  }}
                >
                  Last Updated
                </Typography>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'text.primary' }}>
                  {formatUtcTime(lastUpdated)}
                </Typography>
              </Box>
            )}
            <IconButton
              onClick={fetchProjectDetails}
              disabled={loading}
              sx={{
                width: 34,
                height: 34,
                border: 1,
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <RefreshIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            </IconButton>
            <IconButton
              onClick={onClose}
              sx={{
                width: 34,
                height: 34,
                border: 1,
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <CloseIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: 'background.paper', overflow: 'auto', p: { xs: 2, sm: 3 } }}>
          {loading && !networkData && !lmsData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress size={28} sx={{ color: 'text.secondary' }} />
            </Box>
          ) : (
            <>
              {(error || lmsError) && (
                <Box sx={{ mb: 2 }}>
                  {error && (
                    <Alert severity="error" sx={{ mb: lmsError ? 1 : 0 }}>
                      {error}
                    </Alert>
                  )}
                  {lmsError && <Alert severity="warning">{lmsError}</Alert>}
                </Box>
              )}

              <Box sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, next) => setActiveTab(next)}
                >
                  <Tab label="Network" />
                  <Tab label="LMS" />
                  <Tab label="Alerts" />
                </Tabs>
              </Box>

              {/* NETWORK TAB */}
              {activeTab === 0 && (
                <Box data-demo-id="network-section" sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                  {!networkData ? (
                    <Alert severity="info">No network data available for this project.</Alert>
                  ) : (
                    <>
              {/* System Status Banner */}
              {systemStatus.status !== 'healthy' && (
                <Box sx={{ mb: 3 }}>
                  <Paper
                    sx={{
                      bgcolor: statusTint(systemStatus.accent, 0.08),
                      color: 'text.primary',
                      borderRadius: 2,
                      overflow: 'hidden',
                      position: 'relative',
                      border: `1px solid ${statusTint(systemStatus.accent, 0.3)}`,
                      borderLeft: `3px solid ${systemStatus.accent}`,
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        px: 3,
                        py: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            color: textFor(systemStatus.accent),
                            p: 0.5,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {systemStatus.status === 'critical' ? (
                            <ErrorIcon sx={{ fontSize: 22 }} />
                          ) : (
                            <WarningIcon sx={{ fontSize: 22 }} />
                          )}
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              mb: 0.25,
                              fontSize: '0.9375rem',
                            }}
                          >
                            {systemStatus.label}
                          </Typography>
                          <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                            Connectivity issues detected in {systemStatus.worstType || 'device'} subnet.
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          px: 2,
                          py: 1.2,
                          borderRadius: 1.25,
                          bgcolor: 'background.paper',
                          border: 1,
                          borderColor: 'divider',
                          minWidth: 92,
                          textAlign: 'left',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.62rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.4,
                            color: 'text.secondary',
                            mb: 0.35,
                          }}
                        >
                          Impact
                        </Typography>
                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '1.0rem' }}>
                          {systemStatus.status === 'critical' ? 'High' : 'Low'}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              )}

              {deviceTypeSummaries.some((d) => !d.isSmart && d.total > 0) && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      mb: 1.25,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                    }}
                  >
                    NON-SMART
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                    {deviceTypeSummaries.filter((d) => !d.isSmart && d.total > 0).map(({ type, connected, offline, total }) => (
                      <Box
                        key={type}
                        sx={{
                          p: 1.5,
                          bgcolor: NEUTRAL[50],
                          borderRadius: 1,
                          border: `1px solid ${NEUTRAL[200]}`,
                          minHeight: 88,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.4 }}>
                          {type}
                        </Typography>
                        <Box>
                          <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '1.35rem', color: 'text.primary', lineHeight: 1.1 }}>
                            {formatNumber(total)}
                          </Typography>
                          <Typography sx={{ mt: 0.4, fontSize: '0.68rem', color: 'text.secondary' }}>
                            {formatNumber(connected)} connected · {formatNumber(offline)} offline
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography
                      component="div"
                      sx={{
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: '0.95rem',
                      }}
                    >
                      <Box sx={{ color: 'text.secondary', display: 'flex' }}>
                        <DevicesIcon sx={{ fontSize: 18 }} />
                      </Box>
                      SMART Devices by Type
                    </Typography>
                    <Chip
                      label="Network Monitored"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.62rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        bgcolor: 'transparent',
                        color: 'text.secondary',
                        border: `1px solid ${NEUTRAL[200]}`,
                      }}
                    />
                  </Box>

                  <Paper
                    sx={{
                      borderRadius: 2,
                      border: 1,
                      borderColor: 'divider',
                      overflow: 'hidden',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell sx={{ py: 2, fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', borderColor: 'divider' }}>
                              Device Type
                            </TableCell>
                            <TableCell sx={{ py: 2, fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', width: '36%', borderColor: 'divider' }}>
                              Connectivity
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2, fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', borderColor: 'divider' }}>
                              Count
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2, fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', borderColor: 'divider' }}>
                              Status
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {deviceTypeSummaries
                            .map(({ type, originalType, connected, offline, total, health, isSmart }) => {
                              if (total === 0) return null;

                              const ratio = getConnectivityRatio(connected, offline);
                              const connectedPercent = ratio !== null ? ratio : 0;
                              const offlinePercent = ratio !== null ? 100 - ratio : 0;

                              // Apply same green/red logic to both SMART and NON-SMART
                              const hasOffline = offline > 0;
                              const statusColor = hasOffline ? STATUS.critical : STATUS.healthy;

                              return (
                                <TableRow
                                  key={type}
                                  sx={{
                                    '&:hover': { bgcolor: 'action.hover' },
                                    transition: 'background-color 0.2s',
                                    // Creative distinction: Dashed left border for NON-SMART, solid for SMART
                                    boxShadow: `inset 2px 0 0 ${statusColor}`,
                                  }}
                                >
                                  <TableCell sx={{ borderColor: 'divider' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                      <Box sx={{ 
                                        p: 0.85, 
                                        borderRadius: isSmart ? 1 : '50%', // Circular for NON-SMART
                                        bgcolor: 'action.hover', 
                                        color: 'text.secondary', 
                                        display: 'flex', 
                                        alignItems: 'center' 
                                      }}>
                                        {getDeviceIcon(type)}
                                      </Box>
                                      <Box>
                                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{type}</Typography>
                                        <Typography sx={{ 
                                          fontSize: '0.65rem', 
                                          color: 'text.secondary', 
                                          mt: 0.25,
                                          fontStyle: isSmart ? 'normal' : 'italic' // Italic for NON-SMART
                                        }}>
                                          {isSmart ? 'SMART' : 'NON-SMART'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ borderColor: 'divider' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85 }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', fontFamily: 'monospace', color: 'text.secondary' }}>
                                        <span>{connected} Connected</span>
                                        {offline > 0 ? (
                                          <span style={{ color: textFor(STATUS.critical), fontWeight: 600 }}>{offline} Offline</span>
                                        ) : (
                                          <span style={{ opacity: 0.6 }}>0 Offline</span>
                                        )}
                                      </Box>
                                      <Box sx={{ width: '100%', height: 4, bgcolor: NEUTRAL[100], borderRadius: '9999px', overflow: 'hidden', display: 'flex' }}>
                                        {connectedPercent > 0 && (
                                          <Box
                                            sx={{
                                              height: '100%',
                                              bgcolor: statusColor, // Green/red for both SMART and NON-SMART
                                              width: `${connectedPercent}%`,
                                              transition: 'width 0.3s ease',
                                                                                          }}
                                          />
                                        )}
                                        {offlinePercent > 0 && (
                                          <Box sx={{ height: '100%', bgcolor: NEUTRAL[200], width: `${offlinePercent}%` }} />
                                        )}
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right" sx={{ borderColor: 'divider' }}>
                                    <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{total}</Typography>
                                  </TableCell>
                                  <TableCell align="right" sx={{ borderColor: 'divider' }}>
                                    <Chip
                                      label={health.label}
                                      size="small"
                                      sx={{
                                        bgcolor: statusTint(health.color),
                                        color: textFor(health.color),
                                        border: 'none',
                                        fontWeight: 600,
                                        fontSize: '0.62rem',
                                        height: 24,
                                        '& .MuiChip-label': { px: 1 },
                                        '& .MuiChip-icon': { ml: 1, mr: 0 },
                                      }}
                                      icon={
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: health.color }} />
                                      }
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })
                            .filter(Boolean)}
                          {Object.keys(sourceData || {}).length === 0 && (
                            <TableRow>
                              <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary', borderColor: 'divider' }}>
                                No device type data available
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                      <Paper sx={{ height: '100%', borderRadius: 2, border: 1, borderColor: 'divider', p: 2, bgcolor: 'background.paper' }}>
                      <Typography
                        sx={{
                          mb: 2,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          fontSize: '0.68rem',
                          color: 'text.secondary',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        <SpeedIcon sx={{ fontSize: 16 }} />
                        Network Quality
                      </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {/* Avg Latency Quality */}
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 0.75 }}>
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
                                Avg Latency Quality
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {avgLatencyQuality === null || avgLatencyQuality === undefined ? (
                                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '1rem' }}>-</Typography>
                                ) : (
                                  <>
                                    <Chip
                                      label={avgLatencyQuality > 0.7 ? 'Good' : avgLatencyQuality > 0.4 ? 'Medium' : 'Poor'}
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: '0.55rem',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.6,
                                        bgcolor: `${getLatencyColor(avgLatencyQuality)}1A`,
                                        color: textFor(getLatencyColor(avgLatencyQuality)),
                                        border: `1px solid ${getLatencyColor(avgLatencyQuality)}33`,
                                      }}
                                    />
                                    <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, lineHeight: 1, fontSize: '1.05rem' }}>
                                      {Number(avgLatencyQuality).toFixed(2)}
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ height: 6, width: '100%', bgcolor: 'action.hover', borderRadius: '9999px', overflow: 'hidden' }}>
                              {avgLatencyQuality !== null && avgLatencyQuality !== undefined && (
                                <Box
                                  sx={{
                                    height: '100%',
                                    bgcolor: getLatencyColor(avgLatencyQuality),
                                    width: `${Math.min(avgLatencyQuality * 100, 100)}%`,
                                    borderRadius: '9999px',
                                    boxShadow: `0 0 10px ${getLatencyColor(avgLatencyQuality)}4D`,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>

                          {/* Packet Loss */}
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 0.75 }}>
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
                                Packet Loss
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {packetLoss === null || packetLoss === undefined ? (
                                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '1rem' }}>-</Typography>
                                ) : (
                                  <>
                                    <Chip
                                      label={packetLoss < 1 ? 'Good' : packetLoss < 5 ? 'Medium' : 'Poor'}
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: '0.55rem',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.6,
                                        bgcolor: `${getPacketLossColor(packetLoss)}1A`,
                                        color: textFor(getPacketLossColor(packetLoss)),
                                        border: `1px solid ${getPacketLossColor(packetLoss)}33`,
                                      }}
                                    />
                                    <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, lineHeight: 1, fontSize: '1.05rem' }}>
                                      {Number(packetLoss).toFixed(2)}
                                      <Typography component="span" sx={{ ml: 0.5, color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600 }}>
                                        %
                                      </Typography>
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ height: 6, width: '100%', bgcolor: 'action.hover', borderRadius: '9999px', overflow: 'hidden' }}>
                              {packetLoss !== null && packetLoss !== undefined && (
                                <Box
                                  sx={{
                                    height: '100%',
                                    bgcolor: getPacketLossColor(packetLoss),
                                    width: `${Math.min(packetLoss * 10, 100)}%`,
                                    borderRadius: '9999px',
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ height: '100%', borderRadius: 2, border: 1, borderColor: 'divider', p: 2, bgcolor: 'background.paper' }}>
                    <Typography
                      sx={{
                        mb: 1.5,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        fontSize: '0.68rem',
                        color: 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                      }}
                    >
                      <AccessTimeIcon sx={{ fontSize: 16 }} />
                      Connected Time (SMART)
                    </Typography>
                    {[
                      ['Min', minConnectedTimeSec],
                      ['Average', avgConnectedTimeSec],
                      ['Median', medianConnectedTimeSec],
                    ].map(([label, value], index, rows) => (
                      <Box
                        key={label}
                        sx={{
                          display: 'flex',
                          alignItems: 'baseline',
                          justifyContent: 'space-between',
                          py: 1.1,
                          borderBottom: index < rows.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{label}</Typography>
                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '1.05rem' }}>
                          {value !== null && value !== undefined ? formatConnectedTime(value) : '-'}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ height: '100%', borderRadius: 2, border: 1, borderColor: 'divider', p: 2, bgcolor: 'background.paper' }}>
                    <Typography
                      sx={{
                        mb: 1.5,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        fontSize: '0.68rem',
                        color: 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                      }}
                    >
                      <AccountTreeIcon sx={{ fontSize: 16 }} />
                      Topology Metrics (SMART)
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, pt: 0.5 }}>
                      {[
                        ['Max / CAP', maxNodesPerCap],
                        ['Min / CAP', minNodesPerCap],
                        ['Depth', maxTreeDepth === null || maxTreeDepth === undefined ? null : `${maxTreeDepth}`],
                      ].map(([label, value]) => (
                        <Box key={label}>
                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}>
                            {label}
                          </Typography>
                          <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '1.35rem', mt: 0.75, lineHeight: 1 }}>
                            {value === null || value === undefined ? '-' : value}
                          </Typography>
                          {label === 'Depth' && value !== null && value !== undefined && (
                            <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', mt: 0.35 }}>hops</Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </Grid>

                {/* Software Versions Distribution */}
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <CodeIcon sx={{ fontSize: 18 }} />
                    Software Versions Distribution
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {Object.entries(softwareVersionsByType || {}).map(([deviceType, versions]) => {
                      const versionCount = typeof versions === 'object' && versions !== null ? Object.keys(versions).length : 0;
                      const isExpanded = expandedVersions[deviceType];

                      const deviceTypeData = sourceData?.[deviceType];
                      let deviceTypeTotal = 0;
                      if (deviceTypeData) {
                        if (typeof deviceTypeData === 'object' && deviceTypeData !== null && !Array.isArray(deviceTypeData)) {
                          deviceTypeTotal = (deviceTypeData.connected || 0) + (deviceTypeData.offline || 0);
                        } else if (typeof deviceTypeData === 'number') {
                          deviceTypeTotal = deviceTypeData;
                        }
                      }

                      const fragmentationDetected = versionCount > 2;

                      return (
                        <Paper
                          key={deviceType}
                          sx={{
                            borderRadius: 2,
                            border: 1,
                            borderColor: isExpanded ? NEUTRAL[300] : 'divider',
                            overflow: 'hidden',
                            bgcolor: 'background.paper',
                            transition: 'all 0.2s',
                            '&:hover': { borderColor: NEUTRAL[300] },
                          }}
                        >
                          <Box
                            onClick={() => toggleVersionExpand(deviceType)}
                            sx={{
                              p: 2,
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              '&:hover': { bgcolor: 'action.hover' },
                              transition: 'background-color 0.2s',
                              userSelect: 'none',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <ChevronRightIcon
                                sx={{
                                  color: 'text.secondary',
                                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s',
                                }}
                              />
                              <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>{deviceType}</Typography>
                              {fragmentationDetected ? (
                                <Chip
                                  label="Fragmentation Detected"
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.62rem',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.6,
                                    bgcolor: `${STATUS.critical}1A`,
                                    color: textFor(STATUS.critical),
                                    border: `1px solid ${STATUS.critical}33`,
                                  }}
                                />
                              ) : (
                                <Chip
                                  label={`${versionCount} Version${versionCount !== 1 ? 's' : ''}`}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.62rem',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.6,
                                    bgcolor: 'action.hover',
                                    color: 'text.secondary',
                                    border: 1,
                                    borderColor: 'divider',
                                  }}
                                />
                              )}
                            </Box>
                            <Typography sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.8rem' }}>
                              {deviceTypeTotal} Devices
                            </Typography>
                          </Box>

                          <Collapse in={isExpanded}>
                            <Box sx={{ px: 3, pb: 2.5, pt: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
                              {isExpanded && typeof versions === 'object' && versions !== null && (
                                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                                  {Object.entries(versions)
                                    .sort(([, a], [, b]) => b - a)
                                    .map(([version, count], index) => {
                                      let versionLabel = 'Latest';
                                      let labelColor = STATUS.healthy;
                                      if (index > 0) {
                                        versionLabel = count < deviceTypeTotal * 0.1 ? 'Deprecated' : 'Legacy';
                                        labelColor = count < deviceTypeTotal * 0.1 ? STATUS.critical : STATUS.warning;
                                      }

                                      return (
                                        <Grid item xs={12} sm={6} key={version}>
                                          <Paper
                                            sx={{
                                              p: 1.5,
                                              bgcolor: 'background.paper',
                                              border: 1,
                                              borderColor: 'divider',
                                              borderRadius: 1,
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              alignItems: 'center',
                                            }}
                                          >
                                            <Box>
                                              <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.9rem' }}>
                                                {version}
                                              </Typography>
                                              <Typography
                                                sx={{
                                                  fontSize: '0.62rem',
                                                  fontWeight: 600,
                                                  textTransform: 'uppercase',
                                                  letterSpacing: 0.7,
                                                  color: textFor(labelColor),
                                                  mt: 0.2,
                                                }}
                                              >
                                                {versionLabel}
                                              </Typography>
                                            </Box>
                                            <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                              {count}{' '}
                                              <Typography component="span" sx={{ color: 'text.secondary', fontFamily: 'inherit', fontWeight: 600, fontSize: '0.75rem' }}>
                                                Units
                                              </Typography>
                                            </Typography>
                                          </Paper>
                                        </Grid>
                                      );
                                    })}
                                </Grid>
                              )}
                            </Box>
                          </Collapse>
                        </Paper>
                      );
                    })}

                    {Object.keys(softwareVersionsByType || {}).length === 0 && (
                      <Paper sx={{ p: 3, textAlign: 'center', color: 'text.secondary', border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                        No software version data available
                      </Paper>
                    )}
                  </Box>
                </Grid>
              </Grid>
                    </>
                  )}
                </Box>
              )}

              {/* LMS TAB */}
              {activeTab === 1 && (
                <Box data-demo-id="lms-section" sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                  {!lmsData ? (
                    <Alert severity="info">No LMS data available for this project.</Alert>
                  ) : (
                    <>
                      <Alert 
                        severity="info" 
                        sx={{ 
                          mb: 3,
                          bgcolor: NEUTRAL[50],
                          border: `1px solid ${NEUTRAL[200]}`,
                          color: 'text.primary',
                          '& .MuiAlert-icon': {
                            color: 'text.secondary'
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          NON-SMART Devices - Passive Monitoring
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          These lighting assets do not have network telemetry and do NOT affect project health status, offline ratios, or network quality metrics. 
                          They are displayed for visibility only.
                        </Typography>
                      </Alert>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography
                            component="div"
                            sx={{
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: 0.4,
                              fontSize: '0.75rem',
                              color: 'text.secondary',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <LightbulbIcon sx={{ fontSize: 18 }} />
                            NON-SMART Lighting Assets
                          </Typography>
                          <Chip
                            label="Passive / Not Monitored"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.62rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: 0.6,
                              bgcolor: 'transparent',
                              color: 'text.secondary',
                              border: `1px solid ${NEUTRAL[200]}`,
                            }}
                          />
                        </Box>

                        <Paper sx={{ 
                          borderRadius: 2, 
                          border: `1px solid ${NEUTRAL[200]}`,
                          p: 2, 
                          bgcolor: 'background.paper',
                        }}>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Box sx={{ p: 1.5, bgcolor: NEUTRAL[50], borderRadius: 1, border: `1px solid ${NEUTRAL[200]}`, height: 88, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.4 }}>
                                  Lamps Total (LIGHT)
                                </Typography>
                                <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '1.35rem', color: 'text.primary' }}>
                                  {formatNumber(lmsData.lamps_total)}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ p: 1.5, bgcolor: NEUTRAL[50], borderRadius: 1, border: `1px solid ${NEUTRAL[200]}`, height: 88, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.4 }}>
                                  Assigned Gateways (GW)
                                </Typography>
                                <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '1.35rem', color: 'text.primary' }}>
                                  {formatNumber(lmsData.active_gateways)}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <Box sx={{ flex: 1, p: 1.5, bgcolor: NEUTRAL[50], borderRadius: 1, border: `1px solid ${NEUTRAL[200]}` }}>
                                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.4 }}>
                                    Lamps Online (N/A Health)
                                  </Typography>
                                  <Typography sx={{ mt: 0.3, fontFamily: 'monospace', fontWeight: 600, fontSize: '1.15rem', color: 'text.primary' }}>
                                    {formatNumber(lmsData.lamps_online)}
                                  </Typography>
                                </Box>
                                <Box sx={{ flex: 1, p: 1.5, bgcolor: NEUTRAL[50], borderRadius: 1, border: `1px solid ${NEUTRAL[200]}` }}>
                                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.4 }}>
                                    Lamps Offline (N/A Health)
                                  </Typography>
                                  <Typography sx={{ mt: 0.3, fontFamily: 'monospace', fontWeight: 600, fontSize: '1.15rem', color: 'text.primary' }}>
                                    {formatNumber(lmsData.lamps_offline)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.4 }}>
                                  Proxy
                                </Typography>
                                <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.95rem' }}>
                                  {lmsData.proxy_active ? 'Active' : 'Inactive'}{lmsData.proxy_endpoint ? ` (${lmsData.proxy_endpoint})` : ''}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>

                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography
                            component="div"
                            sx={{
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: 0.4,
                              fontSize: '0.75rem',
                              color: 'text.secondary',
                            }}
                          >
                            LMS Gateways
                          </Typography>
                            <Chip
                            label="NON-SMART Infrastructure"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.62rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: 0.6,
                              bgcolor: 'transparent',
                              color: 'text.secondary',
                              border: `1px solid ${NEUTRAL[200]}`,
                            }}
                          />
                        </Box>

                        <Paper sx={{ 
                          borderRadius: 2, 
                          border: `1px solid ${NEUTRAL[200]}`,
                          overflow: 'hidden', 
                          bgcolor: 'background.paper', 
                        }}>
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                  <TableCell sx={{ py: 2, fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', borderColor: 'divider' }}>
                                    Gateway
                                  </TableCell>
                                  <TableCell sx={{ py: 2, fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', borderColor: 'divider' }}>
                                    Status
                                  </TableCell>
                                  <TableCell sx={{ py: 2, fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', borderColor: 'divider' }}>
                                    Last Seen
                                  </TableCell>
                                  <TableCell align="right" sx={{ py: 2, fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', borderColor: 'divider' }}>
                                    Lamps
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(Array.isArray(lmsData.gateways) ? lmsData.gateways : []).map((gw) => {
                                  const gatewayId = gw?.gateway_id ?? gw?.node_id;
                                  const isExpanded = expandedGateways[gatewayId];
                                  const status = gw?.connection_status || (gw?.is_connected ? 'connected' : 'disconnected');
                                  const statusColor = status === 'disconnected' ? STATUS.critical : STATUS.healthy;
                                  const devices = Array.isArray(gw?.devices) ? gw.devices : [];

                                  return (
                                    <React.Fragment key={String(gatewayId)}>
                                      <TableRow
                                        hover
                                        sx={{ cursor: 'pointer', '& td': { borderColor: 'divider' } }}
                                        onClick={() => toggleGatewayExpand(gatewayId)}
                                      >
                                        <TableCell>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                            <ChevronRightIcon
                                              sx={{
                                                color: 'text.secondary',
                                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s',
                                              }}
                                            />
                                            <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                              {gatewayId ?? '-'}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            size="small"
                                            label={String(status).toUpperCase()}
                                            sx={{
                                              height: 20,
                                              fontSize: '0.55rem',
                                              fontWeight: 600,
                                              letterSpacing: 0.6,
                                              bgcolor: `${statusColor}1A`,
                                              color: textFor(statusColor),
                                              border: `1px solid ${statusColor}33`,
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Typography sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.8rem' }}>
                                            {formatIsoDateTime(gw?.last_seen)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                            {devices.length}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell colSpan={4} sx={{ p: 0, borderColor: 'divider' }}>
                                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                            <Box sx={{ p: 2, bgcolor: 'action.hover', borderTop: 1, borderColor: 'divider' }}>
                                              <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                  <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                                                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.4 }}>
                                                      Software
                                                    </Typography>
                                                    <Typography sx={{ mt: 0.35, fontFamily: 'monospace', fontWeight: 600 }}>
                                                      {gw?.software_version || '-'}
                                                    </Typography>
                                                  </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                  <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                                                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.4 }}>
                                                      Keep Alive
                                                    </Typography>
                                                    <Typography sx={{ mt: 0.35, fontFamily: 'monospace', fontWeight: 600 }}>
                                                      {gw?.keep_alive_ms ?? '-'}{gw?.keep_alive_ms ? ' ms' : ''}
                                                    </Typography>
                                                  </Box>
                                                </Grid>
                                                <Grid item xs={12}>
                                                  <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                                                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.4, mb: 1 }}>
                                                      Lamp Device IDs
                                                    </Typography>
                                                    {devices.length === 0 ? (
                                                      <Typography sx={{ color: 'text.secondary' }}>No devices</Typography>
                                                    ) : (
                                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                        {devices.slice(0, 40).map((id) => (
                                                          <Chip
                                                            key={String(id)}
                                                            size="small"
                                                            label={String(id)}
                                                            sx={{ bgcolor: NEUTRAL[100], border: 1, borderColor: 'divider', color: 'text.secondary', fontFamily: 'monospace' }}
                                                          />
                                                        ))}
                                                        {devices.length > 40 && (
                                                          <Chip
                                                            size="small"
                                                            label={`+${devices.length - 40} more`}
                                                            sx={{ bgcolor: NEUTRAL[100], border: 1, borderColor: 'divider', color: 'text.secondary' }}
                                                          />
                                                        )}
                                                      </Box>
                                                    )}
                                                  </Box>
                                                </Grid>
                                              </Grid>
                                            </Box>
                                          </Collapse>
                                        </TableCell>
                                      </TableRow>
                                    </React.Fragment>
                                  );
                                })}

                                {(Array.isArray(lmsData.gateways) ? lmsData.gateways : []).length === 0 && (
                                  <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary', borderColor: 'divider' }}>
                                      No gateway data available
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>
                      </Grid>
                    </Grid>
                    </>
                  )}
                </Box>
              )}

              {/* ALERTS TAB */}
              {activeTab === 2 && (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Start Time (Local)"
                        type="datetime-local"
                        fullWidth
                        inputProps={{ step: 1 }}
                        value={toLocalInputValue(alertsStartTime)}
                        onChange={(e) => {
                          const dt = fromLocalInputValue(e.target.value);
                          if (dt) setAlertsStartTime(dt);
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="End Time (Local)"
                        type="datetime-local"
                        fullWidth
                        inputProps={{ step: 1 }}
                        value={toLocalInputValue(alertsEndTime)}
                        onChange={(e) => {
                          const dt = fromLocalInputValue(e.target.value);
                          if (dt) setAlertsEndTime(dt);
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth>
                        <InputLabel>Filter</InputLabel>
                        <Select
                          value={alertFilter}
                          label="Filter"
                          onChange={(e) => setAlertFilter(e.target.value)}
                        >
                          <MenuItem value="all">All</MenuItem>
                          <MenuItem value="open">Open</MenuItem>
                          <MenuItem value="closed">Closed</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                      <Button
                        onClick={refreshAlertsToNow}
                        startIcon={<RefreshIcon />}
                        disabled={alertsLoading}
                        sx={{
                          textTransform: 'uppercase',
                          fontSize: '0.62rem',
                          fontWeight: 600,
                          letterSpacing: 0.4,
                          color: 'text.secondary',
                          borderColor: 'divider',
                          width: { xs: '100%', md: 'auto' },
                        }}
                        variant="outlined"
                      >
                        Refresh
                      </Button>
                    </Grid>
                  </Grid>

                  {alertsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress size={28} sx={{ color: 'text.secondary' }} />
                    </Box>
                  ) : alertsError ? (
                    <Alert severity="error">{alertsError}</Alert>
                  ) : (
                    <>
                    {alertsRange && (
                      <AlertSpanChart
                        alerts={deduplicatedAlerts}
                        start={alertsRange.start}
                        end={alertsRange.end}
                        truncated={(alertsData?.events || []).length >= 200}
                        onSelect={(alert) => {
                          setSelectedAlert(alert);
                          setAlertDetailsOpen(true);
                        }}
                      />
                    )}
                    <Paper
                      sx={{
                        borderRadius: 2,
                        border: 1,
                        borderColor: 'divider',
                        overflow: 'hidden',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                              <TableCell
                                sx={{
                                  py: 2,
                                  fontSize: '0.62rem',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.4,
                                  color: 'text.secondary',
                                  borderColor: 'divider',
                                }}
                              >
                                Started
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 2,
                                  fontSize: '0.62rem',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.4,
                                  color: 'text.secondary',
                                  borderColor: 'divider',
                                }}
                              >
                                Duration
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 2,
                                  fontSize: '0.62rem',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.4,
                                  color: 'text.secondary',
                                  borderColor: 'divider',
                                }}
                              >
                                Component
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 2,
                                  fontSize: '0.62rem',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.4,
                                  color: 'text.secondary',
                                  borderColor: 'divider',
                                }}
                              >
                                Severity
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 2,
                                  fontSize: '0.62rem',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.4,
                                  color: 'text.secondary',
                                  borderColor: 'divider',
                                }}
                              >
                                Policy
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 2,
                                  fontSize: '0.62rem',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.4,
                                  color: 'text.secondary',
                                  borderColor: 'divider',
                                }}
                              >
                                State
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {deduplicatedAlerts.map((a) => {
                              const isOpen = a.state === 'open';
                              const alertSeverity = a.severity || 'warning';
                              const alertSeverityStyle = severityColors[alertSeverity] || {};
                              return (
                                <TableRow
                                  key={a.incidentId || a.id}
                                  hover
                                  sx={{ 
                                    '& td': { borderColor: 'divider' }, 
                                    cursor: 'pointer',
                                    bgcolor: 'transparent',
                                    '&:hover': {
                                      bgcolor: 'action.hover',
                                    },
                                  }}
                                  onClick={() => {
                                    setSelectedAlert(a);
                                    setAlertDetailsOpen(true);
                                  }}
                                >
                                  <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.8rem' }}>
                                    {formatReceivedAt(a.startedAt)}
                                  </TableCell>
                                  <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.8rem' }}>
                                    {formatDuration(a.startedAt, a.endedAt)}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={componentDisplayNames[a.component] || a.component || '-'}
                                      size="small"
                                      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 500 }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={alertSeverity}
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        bgcolor: alertSeverityStyle.bg,
                                        color: alertSeverityStyle.color,
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{a.policyName || '-'}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={a.state || '-'}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        bgcolor: isOpen ? statusTint(STATUS.critical) : NEUTRAL[100],
                                        color: textFor(isOpen ? STATUS.critical : 'text.secondary'),
                                        fontWeight: 600,
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}

                            {deduplicatedAlerts.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary', borderColor: 'divider' }}>
                                  No alerts in the selected range.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                    </>
                  )}

                  <AlertDetailsDialog
                    open={alertDetailsOpen}
                    onClose={() => setAlertDetailsOpen(false)}
                    event={selectedAlert}
                    demoDataId="alert-details-content"
                  />
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', px: 2.5, py: 2 }}>
          <Button 
            onClick={fetchProjectDetails} 
            startIcon={<RefreshIcon />} 
            disabled={loading}
            sx={{ color: 'text.secondary' }}
          >
            Refresh
          </Button>
          <Button 
            onClick={onClose} 
            variant="contained"

          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectDetailsDialog;

